import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiClient, wsUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function SummaryTab() {
  const [summary, setSummary] = useState(null);
  const [step1, setStep1] = useState(false);
  const [step2, setStep2] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/summary");
      setSummary(data);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    load();
    let ws;
    try {
      ws = new WebSocket(wsUrl());
      ws.onmessage = () => load();
    } catch (_e) { /* noop */ }
    return () => { try { ws?.close(); } catch (_e) { /* noop */ } };
  }, []);

  const resetAll = async () => {
    try {
      await apiClient.post("/admin/reset", { pin: "1234" });
      toast.success("Today's data reset");
      setStep1(false); setStep2(false);
      load();
    } catch {
      toast.error("Reset failed");
    }
  };

  if (!summary) return <p className="text-neutral-500">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="summary-tab">
      <div className="flex flex-wrap items-baseline gap-4">
        <h3 className="font-display text-2xl text-neutral-900">Today&apos;s Summary</h3>
        <span className="text-sm text-neutral-500">{summary.date} (IST)</span>
        <span className="kg-badge-red" data-testid="summary-drawn-count">
          {summary.drawn_count}/{summary.total_branches} drawn
        </span>
        <span
          className="px-3 py-1 rounded-full text-white text-xs font-semibold"
          style={{ background: "linear-gradient(135deg, #f09433 0%, #dc2743 50%, #bc1888 100%)" }}
          data-testid="summary-instagram-count"
        >
          📸 {summary.instagram_followers_today || 0} IG followers gained today
        </span>
        <span className="text-sm text-neutral-500">· {summary.total_entries || 0} total entries</span>
      </div>

      <div
        className="kg-card p-4 flex flex-wrap gap-x-6 gap-y-2 items-center"
        data-testid="tomorrow-pool-strip"
      >
        <span className="text-sm font-semibold text-neutral-800">
          Tomorrow&apos;s draw pool ({summary.tomorrow_date || ""}):
        </span>
        <span className="kg-badge-red">{summary.tomorrow_pool_size || 0} entries collected today</span>
        <span className="text-xs text-neutral-500">
          Bills generated today are eligible for tomorrow morning&apos;s draw.
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="summary-grid">
        {summary.rows.map((r, i) => (
          <div
            key={i}
            className={`kg-card p-4 ${r.winner ? "kg-card-red-left" : ""}`}
            style={{ borderLeftColor: r.winner ? "#CC0000" : undefined }}
            data-testid={`summary-card-${i}`}
          >
            <p className="text-sm font-semibold text-neutral-900">{r.branch}</p>
            <p className="text-xs text-neutral-500 mt-1">{r.entries} entries</p>
            <div className="mt-3">
              {r.winner ? (
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#CC0000] font-semibold">Winner</p>
                  <p className="font-display text-xl text-neutral-900 mt-0.5">{r.winner.name}</p>
                </div>
              ) : (
                <p className="text-sm text-neutral-400 italic">Pending draw</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="kg-card p-6 mt-6 border-red-200">
        <h4 className="font-display text-lg text-neutral-900">Danger Zone</h4>
        <p className="text-sm text-neutral-600 mt-1">Resets all today&apos;s registrations and winners. Cannot be undone.</p>
        <Button
          onClick={() => setStep1(true)}
          className="mt-4 bg-[#CC0000] hover:bg-[#990000] text-white"
          data-testid="reset-all-btn"
        >
          Reset All Data for Today
        </Button>
      </div>

      <AlertDialog open={step1} onOpenChange={setStep1}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset today&apos;s data?</AlertDialogTitle>
            <AlertDialogDescription>
              All registrations and winners for today will be deleted permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-step1-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setStep1(false); setStep2(true); }}
              className="bg-[#CC0000] hover:bg-[#990000]"
              data-testid="reset-step1-confirm"
            >Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={step2} onOpenChange={setStep2}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This is the final confirmation. All data for today across all 17 branches will be wiped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-step2-cancel">No, keep data</AlertDialogCancel>
            <AlertDialogAction
              onClick={resetAll}
              className="bg-[#CC0000] hover:bg-[#990000]"
              data-testid="reset-step2-confirm"
            >Yes, wipe today&apos;s data</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
